import React, { useState } from "react";
import { Control, Controller, FieldValues, Path, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import FileInput from "components/form_components/FileInput";
import Button from "components/ui/button/Button";
import { FileWithDirectoryPath } from "lib/hooks/api/customer_documents/types";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { UIcon } from "lib/icons/untitled_ui/UIcon";
import { useDocumentUploadProgress } from "../DocumentUploadProgressProvider";
import {
  getInFlightFileRowStatus,
  getInFlightFileRowTooltip,
  getPersistedDocumentRowStatus,
} from "./UploadCustomerDocumentsForm.utils";
import UploadedDocumentRow from "./UploadedDocumentRow";

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T, unknown>;
  isLoading: boolean;
  maxHeight?: number; // Optional prop to set max height of the file list
  includeDescription?: boolean; // Optional prop to include description
  existingDocuments?: CustomerDocumentDetails[];
  exampleLink?: {
    buttonText: string;
    url: string;
  };
};

export default function UploadCustomerDocumentsForm<T extends FieldValues>({
  name,
  control,
  isLoading,
  maxHeight = 400, // Default max height for the file list
  includeDescription = true, // Default to true if not provided
  existingDocuments = [],
  exampleLink,
}: Props<T>) {
  const { field } = useController({
    name, // The field name from UploadDocumentsForm
    control,
  });

  const files = field.value;

  const [fileErrors, setFileErrors] = useState<string[]>([]);

  const { uploadStatuses, addStatusesForFiles, removeStatusForFile } = useDocumentUploadProgress();

  const existingFileNames = new Set(existingDocuments.map((d) => d.fileName));
  const filesNotYetPersisted = (files as FileWithDirectoryPath[]).filter(
    (f) => !existingFileNames.has(f.name),
  );
  const totalDocumentsCount = existingDocuments.length + filesNotYetPersisted.length;

  const addNewFiles = (newFiles: FileWithDirectoryPath[]) => {
    if (isLoading) {
      return;
    }
    field.onChange(newFiles);
    addStatusesForFiles(newFiles);
  };

  const handleDeleteFileByName = (fileName: string) => {
    const updatedFiles: FileWithDirectoryPath[] = (files as FileWithDirectoryPath[]).filter(
      (f) => f.name !== fileName,
    );
    field.onChange(updatedFiles);
    removeStatusForFile(fileName);
  };

  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <FileInput
            {...field}
            inputProps={{ id: "tender-upload-input", label: "Tender upload input" }}
            acceptedFileTypes=".csv, .doc, .docx, .pptx, .xlsx, .odt, .odp, .ods, .pdf, .zip"
            errorMessage="Only certain file types are accepted"
            onChange={(files) => addNewFiles(files)}
            onUnsupportedFilesError={(errors) => {
              setFileErrors(errors);
            }}
            title="Click or drag tender documents here to upload"
            listFileNamesInline={false}
          />
        )}
      />

      {exampleLink && (
        <Button as="a" href={exampleLink.url} target="_blank" buttonType="secondary">
          {exampleLink.buttonText} <UIcon icon="linkExternal02" />
        </Button>
      )}

      {fileErrors.length > 0 && (
        <ErrorContainer>
          <Text>Invalid file types:</Text>
          {fileErrors.map((error, index) => (
            <Text key={index}>{error}</Text>
          ))}
        </ErrorContainer>
      )}

      {includeDescription && (
        <UploadedDocumentsText>
          <Text h3>Uploaded documents {totalDocumentsCount > 0 && `(${totalDocumentsCount})`}</Text>
          {totalDocumentsCount === 0 && <Text>Your uploaded documents will appear here</Text>}
        </UploadedDocumentsText>
      )}

      <FilesList maxHeight={maxHeight}>
        {existingDocuments.map((doc) => {
          const status = getPersistedDocumentRowStatus(doc.status);
          return <UploadedDocumentRow key={doc.id} name={doc.fileName} status={status} />;
        })}

        {filesNotYetPersisted.map((file: File) => {
          const fileStatus = uploadStatuses.find((s) => s.file.name === file.name);

          const status = getInFlightFileRowStatus({ isLoading, fileStatus });

          return (
            <UploadedDocumentRow
              key={file.name}
              name={file.name}
              tooltip={getInFlightFileRowTooltip(fileStatus)}
              status={status}
              allowDelete={status === "idle"}
              onDelete={() => handleDeleteFileByName(file.name)}
            />
          );
        })}
      </FilesList>
    </>
  );
}

const FilesList = styled(Flex)<{ maxHeight: number }>(({ theme, maxHeight }) => ({
  gap: 16,
  flexDirection: "column",
  maxHeight,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: 7,
    float: "right",
  },
  "&::-webkit-scrollbar-track": {
    boxShadow: "inset 0 0 0px $placeholder-grey",
    borderRadius: 7,
  },

  "&::-webkit-scrollbar-thumb": {
    background: theme.colors.sysTextPlaceholder,
    borderRadius: 7,
  },

  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.colors.sysTextPlaceholder,
  },
}));

const ErrorContainer = styled(Flex)(({ theme }) => ({
  color: theme.colors.sysDestructiveDefault,
  fontWeight: 500,
  lineHeight: 22,
  marginBottom: 8,
  gap: 8,
}));

const UploadedDocumentsText = styled(Flex)({
  gap: 8,
  flexDirection: "column",
  marginTop: 16,
});
